import {
  apiDateTimeToCalendarDate,
  getTodayForInput,
  type CalendarDate,
} from '@/shared/lib/calendarDate';
import type { ActionLogEntry } from '@/types';

export interface ActionLogDayGroup {
  date: CalendarDate;
  dayLabel: string;
  items: ActionLogEntry[];
  hiddenCount?: number;
}

export interface ActionLogPreview {
  groups: ActionLogDayGroup[];
  totalHiddenCount: number;
}

const DAY_LABEL_FORMATTER = new Intl.DateTimeFormat('en', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

const yesterdayOf = (today: CalendarDate): CalendarDate => {
  const local = new Date(`${today}T00:00:00`);
  local.setDate(local.getDate() - 1);
  const year = local.getFullYear();
  const month = String(local.getMonth() + 1).padStart(2, '0');
  const day = String(local.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}` as CalendarDate;
};

const formatDayLabel = (date: CalendarDate, today: CalendarDate): string => {
  if (date === today) return 'Today';
  if (date === yesterdayOf(today)) return 'Yesterday';
  return DAY_LABEL_FORMATTER.format(new Date(`${date}T00:00:00`));
};

const getTimestampMillis = (timestamp: string): number | null => {
  const value = Date.parse(timestamp);
  return Number.isNaN(value) ? null : value;
};

const sortActionLogEntries = (entries: ActionLogEntry[]): ActionLogEntry[] => {
  return [...entries].sort((a, b) => {
    const aTimestamp = getTimestampMillis(a.timestamp);
    const bTimestamp = getTimestampMillis(b.timestamp);

    if (aTimestamp !== null && bTimestamp !== null) {
      return bTimestamp - aTimestamp;
    }

    if (aTimestamp !== null) return -1;
    if (bTimestamp !== null) return 1;

    return b.timestamp.localeCompare(a.timestamp);
  });
};

export const groupByDay = (
  entries: ActionLogEntry[],
  today: CalendarDate = getTodayForInput()
): ActionLogDayGroup[] => {
  const sorted = sortActionLogEntries(entries);
  const byDate = new Map<CalendarDate, ActionLogEntry[]>();

  for (const entry of sorted) {
    const date = apiDateTimeToCalendarDate(entry.timestamp);
    if (!date) continue;
    const bucket = byDate.get(date);
    if (bucket) {
      bucket.push(entry);
    } else {
      byDate.set(date, [entry]);
    }
  }

  return Array.from(byDate.entries()).map(([date, items]) => ({
    date,
    dayLabel: formatDayLabel(date, today),
    items,
  }));
};

export const getActionLogPreview = (
  entries: ActionLogEntry[],
  limit: number,
  today: CalendarDate = getTodayForInput()
): ActionLogPreview => {
  const groups = groupByDay(entries, today);
  const totalCount = groups.reduce((sum, group) => sum + group.items.length, 0);
  const normalizedLimit = Math.max(0, limit);
  let remaining = normalizedLimit;
  let visibleCount = 0;
  const previewGroups: ActionLogDayGroup[] = [];

  for (const group of groups) {
    if (remaining <= 0) break;

    const items = group.items.slice(0, remaining);
    const hiddenCount = group.items.length - items.length;

    previewGroups.push({
      ...group,
      items,
      hiddenCount: hiddenCount > 0 ? hiddenCount : undefined,
    });

    visibleCount += items.length;
    remaining -= items.length;

    if (hiddenCount > 0) break;
  }

  return {
    groups: previewGroups,
    totalHiddenCount: Math.max(0, totalCount - visibleCount),
  };
};
